import React from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, Button, Hourglass03 } from '@opendate-app/setlist';
import { conductorTheme } from '../themeTokens';

const COPY = {
  suggest: 'This conversation is getting long — start a new chat with a summary, or compact this one.',
  urgent: 'Long conversations are more likely to slow down or fail. Start a new chat with a summary.'
};

const ContextHeadroomBanner = ({
  contextUsage,
  onNewChatWithSummary,
  onCompact,
  isCreatingHandoff,
  isCompacting,
  actionsDisabled,
  disabledReason
}) => {
  const thresholdState = contextUsage?.threshold_state;
  if (thresholdState !== 'suggest' && thresholdState !== 'urgent') return null;

  const isUrgent = thresholdState === 'urgent';
  const busy = isCreatingHandoff || isCompacting;
  const disabled = actionsDisabled || busy;
  const buttonTitle = actionsDisabled ? disabledReason : undefined;

  return (
    <div
      role="status"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '10px',
        padding: '10px 12px',
        borderRadius: '10px',
        border: `1px solid ${conductorTheme.borderBlue}`,
        backgroundColor: conductorTheme.accentSurfaceSoft,
        color: conductorTheme.textSlate
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          flex: '1 1 240px',
          fontSize: '12.5px',
          lineHeight: 1.4,
          fontWeight: 600
        }}
      >
        {isUrgent ? (
          <AlertTriangle width={14} height={14} aria-hidden="true" style={{ color: conductorTheme.accentText, flexShrink: 0 }} />
        ) : (
          <Hourglass03 width={14} height={14} aria-hidden="true" style={{ color: conductorTheme.accentText, flexShrink: 0 }} />
        )}
        {COPY[thresholdState]}
      </span>

      <span style={{ display: 'inline-flex', gap: '8px', flexShrink: 0 }}>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={onNewChatWithSummary}
          disabled={disabled}
          title={buttonTitle}
        >
          {isCreatingHandoff ? 'Summarizing…' : 'New chat with summary'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onCompact}
          disabled={disabled}
          title={buttonTitle}
        >
          {isCompacting ? 'Compacting…' : 'Compact this thread'}
        </Button>
      </span>
    </div>
  );
};

ContextHeadroomBanner.propTypes = {
  contextUsage: PropTypes.shape({
    threshold_state: PropTypes.string
  }),
  onNewChatWithSummary: PropTypes.func.isRequired,
  onCompact: PropTypes.func.isRequired,
  isCreatingHandoff: PropTypes.bool,
  isCompacting: PropTypes.bool,
  actionsDisabled: PropTypes.bool,
  disabledReason: PropTypes.string
};

ContextHeadroomBanner.defaultProps = {
  contextUsage: null,
  isCreatingHandoff: false,
  isCompacting: false,
  actionsDisabled: false,
  disabledReason: undefined
};

export default ContextHeadroomBanner;
